// src/utils/smoothScroll.js
/**
 * High-quality smooth scrolling utility with easing
 */

/**
 * Easing function for smooth animation
 * @param {number} t - Current time (0-1)
 * @returns {number} - Eased value
 */
const easeInOutCubic = (t) => {
  return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
};

/**
 * Smooth scroll to a specific position
 * @param {number} targetPosition - Target scroll position
 * @param {number} duration - Animation duration in milliseconds (default: 800)
 */
export const smoothScrollTo = (targetPosition, duration = 800) => {
  const startPosition = window.pageYOffset;
  const distance = targetPosition - startPosition;
  let startTime = null;

  const animation = (currentTime) => {
    if (startTime === null) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);
    const ease = easeInOutCubic(progress);

    window.scrollTo(0, startPosition + distance * ease);

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }
  };

  requestAnimationFrame(animation);
};

/**
 * Smooth scroll to an element
 * @param {string|HTMLElement} target - CSS selector or DOM element
 * @param {Object} options - Scroll options
 * @param {number} options.duration - Animation duration in milliseconds
 * @param {number} options.offset - Offset from target (e.g., for fixed headers)
 */
export const smoothScrollToElement = (target, options = {}) => {
  const {
    duration = 800,
    offset = 80, // Default offset for fixed header
  } = options;

  const element = typeof target === 'string'
    ? document.querySelector(target)
    : target;

  if (!element) {
    console.warn(`Element not found: ${target}`);
    return;
  }

  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;

  smoothScrollTo(offsetPosition, duration);
};

/**
 * Initialize smooth scroll for all anchor links
 * @param {Object} options - Configuration options
 */
export const initSmoothScroll = (options = {}) => {
  const {
    duration = 800,
    offset = 80,
    selector = 'a[href^="#"]', // All anchor links starting with #
  } = options;

  document.addEventListener('click', (e) => {
    const anchor = e.target.closest(selector);

    if (!anchor) return;

    const href = anchor.getAttribute('href');

    // Skip if it's just "#" or external link
    if (!href || href === '#' || href.startsWith('http')) return;

    const targetElement = document.querySelector(href);

    if (targetElement) {
      e.preventDefault();
      smoothScrollToElement(targetElement, { duration, offset });

      // Update URL without jumping
      if (window.history && window.history.pushState) {
        window.history.pushState(null, null, href);
      }
    }
  });
};

/**
 * Smooth scroll to top
 * @param {number} duration - Animation duration in milliseconds
 */
export const smoothScrollToTop = (duration = 800) => {
  smoothScrollTo(0, duration);
};

const smoothScroll = {
  smoothScrollTo,
  smoothScrollToElement,
  initSmoothScroll,
  smoothScrollToTop,
};

export default smoothScroll;
